/**
 * BudgetPanel — Redesigned AI Usage & Budget Control Screen.
 *
 * This is a CONTROL screen (not an analytics dashboard — the Dashboard owns charts):
 *   1. Header: title, subtitle, View History, Edit Configuration
 *   2. Filter bar: Scope selector (Global / Agent) with inheritance badge,
 *      Month period selector with billing-cycle label, Last-modified text
 *   3. Three cards:
 *      a. Monthly Spend Cap (spend / cap, % used, progress bar with soft alert tick,
 *         projection, daily avg, sparkline, link to dashboard)
 *      b. Rate Limits (live RPM / TPM polling every 15s)
 *      c. When the cap is reached (action, resolved fallback target, soft alert, notify)
 *   4. Provider banner (when any provider lacks an API key)
 *   5. Model usage & limits table (tabs By Model | By Agent | By Surface, grouping,
 *      per-model caps, export CSV)
 *   6. Bottom row:
 *      - Recent budget & enforcement activity (last 10 safety events)
 *      - Pricing status list (Built-in / Custom / Self-hosted $0) + Override Price drawer
 */
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import { useSearchParams } from 'react-router'
import {
  DollarSign,
  SlidersHorizontal,
  FlaskConical,
  Pencil,
  History,
  Activity,
  Loader2,
  Wallet,
} from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import RequirePermission from '../../auth/RequirePermission.js'
import ResourceDrawer from '../ResourceDrawer.js'
import Select from '../../ui/Select.js'
import { TIMEZONE_OPTIONS, AUTO_TIMEZONE } from '../../../utils/timezone.js'
import {
  DRAWER_INPUT_CLASS,
  DRAWER_LABEL_CLASS,
  ScopeBar,
  InheritingBanner,
  LastModifiedLabel,
  DrawerTabs,
  TestPanel,
  VersionHistoryDrawer,
  type ScopeAgent,
  type TestTraceResult,
} from './versioned-config-ui.js'

import PeriodSelector from './budget/PeriodSelector.js'
import HeroBudgetCard from './budget/HeroBudgetCard.js'
import ModelUsageTable, { type ModelUsageRow } from './budget/ModelUsageTable.js'
import ModelLimitDrawer from './budget/ModelLimitDrawer.js'
import ActivityPricingDrawer from './budget/ActivityPricingDrawer.js'
import type { PricingStatusItem } from './budget/PricingStatusCard.js'
import type { BudgetActivityEvent } from './budget/ActivityTable.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CapExceededAction =
  | 'fallback_to_free_models'
  | 'block_all_requests'
  | 'alert_only'
  | 'degrade_to_cheaper_model'

interface BudgetConfigInput {
  monthlySpendCapUsd: number
  softAlertThresholdPercent: number
  capExceededAction: CapExceededAction
  rateLimits: {
    maxRequestsPerMinute: number
    maxTokensPerMinute: number
    softAlertThresholdPercent: number
  }
  billingCycleDay: number
  timezone: string
}

interface BudgetConfig extends BudgetConfigInput {
  id: string
  scope: 'global' | 'agent'
  agentId: string | null
  version: number
  isActive: boolean
  createdBy: string
  createdAt: string
}

interface BudgetActionMeta {
  value: CapExceededAction
  label: string
  hint: string
}

interface BudgetMeta {
  capExceededActions: BudgetActionMeta[]
}

interface BudgetSummaryResponse {
  period: string
  cycleLabel: string
  isCurrentMonth: boolean
  totalSpendUsd: number
  monthlySpendCapUsd: number
  percentUsed: number
  projectedSpendUsd: number
  state: 'safe' | 'will_exceed'
  willExceedDate: string | null
  dailyAverageUsd: number
  daysLeft: number
  dailySpend: Array<{ date: string; spendUsd: number }>
  capExceededAction: CapExceededAction
  resolvedFallbackTarget: { provider: string; model: string; isConfigured: boolean } | null
  fallbackWarning: string | null
  softAlertThresholdPercent: number
  softAlertUsd: number
  notifyChannels: string[]
  unresolvedIssuesCount?: number
  estimatedUsageCount?: number
  config: BudgetConfig
  inheriting: boolean
}

interface UsageBreakdownResponse {
  period: string
  totals: {
    requests: number
    inputTokens: number
    outputTokens: number
    costUsd: number
  }
  providerBanner: {
    missingCount: number
    totalCount: number
    providerNames: string[]
    message: string
  } | null
  rows: ModelUsageRow[]
}

interface ActivityResponse {
  events: BudgetActivityEvent[]
}

interface PricingListResponse {
  models: PricingStatusItem[]
}

function toInput(config: BudgetConfig): BudgetConfigInput {
  return {
    monthlySpendCapUsd: config.monthlySpendCapUsd,
    softAlertThresholdPercent: config.softAlertThresholdPercent,
    capExceededAction: config.capExceededAction,
    rateLimits: config.rateLimits,
    billingCycleDay: config.billingCycleDay ?? 1,
    timezone: config.timezone ?? 'UTC',
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function BudgetPanel(): JSX.Element {
  const toast = useToast()

  // Scope & Period State
  const [agents, setAgents] = useState<ScopeAgent[]>([])
  const [scope, setScope] = useState<'global' | 'agent'>('global')
  const [scopeAgentId, setScopeAgentId] = useState<string | null>(null)
  const [period, setPeriod] = useState<string>(() => new Date().toISOString().slice(0, 7))

  // Data State
  const [meta, setMeta] = useState<BudgetMeta | null>(null)
  const [config, setConfig] = useState<BudgetConfig | null>(null)
  const [form, setForm] = useState<BudgetConfigInput | null>(null)
  const [inheriting, setInheriting] = useState(false)
  const [summary, setSummary] = useState<BudgetSummaryResponse | null>(null)
  const [breakdown, setBreakdown] = useState<UsageBreakdownResponse | null>(null)
  const [pricingItems, setPricingItems] = useState<PricingStatusItem[]>([])
  const [activityEvents, setActivityEvents] = useState<BudgetActivityEvent[]>([])

  // UI State
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [savingConfig, setSavingConfig] = useState(false)
  const [tableTab, setTableTab] = useState<'model' | 'provider' | 'agent' | 'surface'>('model')
  const [selectedProvider, setSelectedProvider] = useState<string>('')

  // Drawers
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false)
  const [drawerTab, setDrawerTab] = useState<'config' | 'test'>('config')
  const [historyOpen, setHistoryOpen] = useState(false)
  const [versions, setVersions] = useState<BudgetConfig[]>([])

  const [modelLimitDrawerOpen, setModelLimitDrawerOpen] = useState(false)
  const [editingModelLimit, setEditingModelLimit] = useState<{
    model: string
    currentCap?: number | undefined
    currentAction?: 'block' | 'degrade' | undefined
    currentTargetModel?: string | null | undefined
  } | null>(null)
  const [savingModelLimit, setSavingModelLimit] = useState(false)

  const [savingPricing, setSavingPricing] = useState(false)

  // URL-synced Activity & Pricing drawer
  const [searchParams, setSearchParams] = useSearchParams()
  const panelParam = searchParams.get('panel')
  const isActivityPricingOpen = panelParam === 'activity' || panelParam === 'pricing'
  const activityPricingTab: 'activity' | 'pricing' =
    panelParam === 'pricing' ? 'pricing' : 'activity'

  function handleOpenPanel(tab: 'activity' | 'pricing'): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('panel', tab)
      return next
    })
  }

  function handleClosePanel(): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('panel')
      return next
    })
  }

  // Test Drawer State
  const [testSpend, setTestSpend] = useState('')
  const [testRpm, setTestRpm] = useState('')
  const [testTpm, setTestTpm] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<TestTraceResult | null>(null)

  const dirty = useMemo(
    () =>
      config !== null &&
      form !== null &&
      JSON.stringify(form) !== JSON.stringify(toInput(config)),
    [config, form],
  )

  const configuredProviderNames = useMemo(() => {
    const fromPricing = pricingItems.map((p) => p.provider)
    const fromRows = (breakdown?.rows ?? []).map((r) => r.provider).filter(Boolean)
    return Array.from(new Set([...fromPricing, ...fromRows])).sort()
  }, [pricingItems, breakdown?.rows])

  const configuredModelNames = useMemo(() => {
    return (breakdown?.rows ?? [])
      .filter((r) => r.taskType !== 'unattributed')
      .map((r) => r.key)
  }, [breakdown?.rows])

  // Scope query string helper
  const scopeQuery = useCallback(() => {
    return scope === 'agent' && scopeAgentId
      ? `scope=agent&agentId=${encodeURIComponent(scopeAgentId)}`
      : 'scope=global'
  }, [scope, scopeAgentId])

  // Initial loads: agents and meta
  useEffect(() => {
    api
      .get<{ agents: ScopeAgent[] }>('/admin/ai/agents')
      .then((res) => setAgents(res.agents ?? []))
      .catch(() => setAgents([]))
    api
      .get<BudgetMeta>('/admin/ai/budget/meta')
      .then((res) => setMeta(res))
      .catch(() => setMeta(null))
  }, [])

  // Load summary and config
  const loadSummary = useCallback(async () => {
    try {
      const res = await api.get<BudgetSummaryResponse>(
        `/admin/ai/budget/summary?${scopeQuery()}&period=${period}`,
      )
      setSummary(res)
      setConfig(res.config)
      setForm(toInput(res.config))
      setInheriting(res.inheriting)
    } catch (err) {
      toast.error('Failed to load budget summary', String(err))
    }
  }, [scopeQuery, period, toast])

  // Load usage breakdown
  const loadBreakdown = useCallback(async () => {
    setTableLoading(true)
    try {
      const providerParam = selectedProvider ? `&provider=${encodeURIComponent(selectedProvider)}` : ''
      const res = await api.get<UsageBreakdownResponse>(
        `/admin/ai/budget/usage-breakdown?${scopeQuery()}&period=${period}&groupBy=${tableTab}${providerParam}`,
      )
      setBreakdown(res)
    } catch (err) {
      toast.error('Failed to load usage breakdown', String(err))
    } finally {
      setTableLoading(false)
    }
  }, [scopeQuery, period, tableTab, selectedProvider, toast])

  // Load pricing status
  const loadPricing = useCallback(async () => {
    try {
      const res = await api.get<PricingListResponse>('/admin/ai/pricing')
      setPricingItems(res.models ?? [])
    } catch {
      setPricingItems([])
    }
  }, [])

  // Load recent activity
  const loadActivity = useCallback(async () => {
    try {
      const res = await api.get<ActivityResponse>(
        `/admin/ai/budget/activity?period=${period}&limit=50`,
      )
      setActivityEvents(res.events ?? [])
    } catch {
      setActivityEvents([])
    }
  }, [period])

  // Master load trigger
  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      await Promise.all([loadSummary(), loadBreakdown(), loadPricing(), loadActivity()])
    } finally {
      setLoading(false)
    }
  }, [loadSummary, loadBreakdown, loadPricing, loadActivity])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  // Save budget configuration version
  async function handleSaveConfig(): Promise<void> {
    if (!config || !form || !dirty) return
    setSavingConfig(true)
    try {
      const res = await api.put<{ config: BudgetConfig; inheriting: boolean }>('/admin/ai/budget', {
        scope,
        agentId: scope === 'agent' ? scopeAgentId : null,
        config: form,
      })
      setConfig(res.config)
      setForm(toInput(res.config))
      setInheriting(false)
      setConfigDrawerOpen(false)
      toast.success(
        'Budget configuration saved',
        scope === 'agent'
          ? `Agent override saved as v${res.config.version}.`
          : `Global configuration saved as v${res.config.version}.`,
      )
      await loadSummary()
    } catch (err) {
      toast.error('Failed to save budget settings', String(err))
    } finally {
      setSavingConfig(false)
    }
  }

  // Version history & restore
  async function openHistory(): Promise<void> {
    try {
      const res = await api.get<{ versions: BudgetConfig[] }>(
        `/admin/ai/budget/history?${scopeQuery()}`,
      )
      setVersions(res.versions)
      setHistoryOpen(true)
    } catch (err) {
      toast.error('Failed to load version history', String(err))
    }
  }

  async function handleRestore(versionId: string): Promise<void> {
    try {
      const res = await api.post<{ config: BudgetConfig }>('/admin/ai/budget/restore', { versionId })
      setConfig(res.config)
      setForm(toInput(res.config))
      setInheriting(false)
      setHistoryOpen(false)
      toast.success('Version restored', `Rolled back — now active as v${res.config.version}.`)
      await loadSummary()
    } catch (err) {
      toast.error('Failed to restore version', String(err))
    }
  }

  // Dry-run test panel
  async function runTest(): Promise<void> {
    if (!form || testSpend.trim() === '') return
    setTesting(true)
    setTestResult(null)
    try {
      const res = await api.post<TestTraceResult>('/admin/ai/budget/test', {
        config: form,
        spendUsd: parseFloat(testSpend) || 0,
        ...(testRpm.trim() !== '' ? { requestsPerMinute: parseInt(testRpm) || 0 } : {}),
        ...(testTpm.trim() !== '' ? { tokensPerMinute: parseInt(testTpm) || 0 } : {}),
      })
      setTestResult(res)
    } catch (err) {
      toast.error('Test failed', String(err))
    } finally {
      setTesting(false)
    }
  }

  // Model Limit Handler
  function handleOpenEditCap(
    model: string,
    currentCap?: number,
    currentAction?: 'block' | 'degrade',
    currentTargetModel?: string | null,
  ): void {
    setEditingModelLimit({
      model,
      currentCap,
      currentAction: currentAction ?? 'block',
      currentTargetModel,
    })
    setModelLimitDrawerOpen(true)
  }

  async function handleSaveModelLimit(
    model: string,
    monthlyCapUsd: number,
    action: 'block' | 'degrade',
    targetModel?: string | null,
  ): Promise<void> {
    setSavingModelLimit(true)
    try {
      await api.put('/admin/ai/budget/model-limits', {
        model,
        monthlyCapUsd,
        action,
        targetModel,
      })
      toast.success('Model limit saved', `Monthly cap for ${model} set to $${monthlyCapUsd.toFixed(2)}.`)
      setModelLimitDrawerOpen(false)
      await loadBreakdown()
    } catch (err) {
      toast.error('Failed to save model limit', String(err))
    } finally {
      setSavingModelLimit(false)
    }
  }

  async function handleDeleteModelLimit(model: string): Promise<void> {
    try {
      await api.delete(`/admin/ai/budget/model-limits?model=${encodeURIComponent(model)}`)
      toast.success('Model limit removed', `Cap removed for ${model}.`)
      setModelLimitDrawerOpen(false)
      await loadBreakdown()
    } catch (err) {
      toast.error('Failed to remove model limit', String(err))
    }
  }

  // Pricing Override Handlers
  async function handleSavePricing(
    provider: string,
    model: string,
    inputPer1k: number,
    outputPer1k: number,
  ): Promise<void> {
    setSavingPricing(true)
    try {
      await api.put('/admin/ai/pricing', {
        provider,
        model,
        inputPer1k,
        outputPer1k,
      })
      toast.success('Custom price saved', `Rates updated for ${model}.`)
      await Promise.all([loadPricing(), loadBreakdown()])
    } catch (err) {
      toast.error('Failed to save custom price', String(err))
    } finally {
      setSavingPricing(false)
    }
  }

  async function handleDeletePricing(provider: string, model: string): Promise<void> {
    try {
      await api.delete(
        `/admin/ai/pricing?provider=${encodeURIComponent(provider)}&model=${encodeURIComponent(model)}`,
      )
      toast.success('Price reset', `Reverted ${model} to built-in rate.`)
      await Promise.all([loadPricing(), loadBreakdown()])
    } catch (err) {
      toast.error('Failed to reset price', String(err))
    }
  }

  function patchForm(partial: Partial<BudgetConfigInput>): void {
    if (!form) return
    setForm({ ...form, ...partial })
  }

  if (loading || !config || !form || !summary || !breakdown) {
    return (
      <div className="flex items-center gap-2 py-16 text-sm text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading AI budget and usage controls…
      </div>
    )
  }

  const scopeTitle =
    scope === 'global' ? 'Global (all agents)' : `Agent: ${scopeAgentId}`

  return (
    <div className="space-y-4">
      {/* 1. Header: title, subtitle, Buttons right to left: Edit config, History, Pricing, Activity */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Usage & Budget</h3>
          <p className="text-xs text-gray-500">
            Spend caps, model limits and alerts enforced in the request pipeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Activity button with red count badge */}
          <button
            type="button"
            className="relative inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-2xs transition-colors hover:bg-gray-50"
            onClick={() => handleOpenPanel('activity')}
          >
            <Activity className="h-3.5 w-3.5" />
            Activity
            {(summary.unresolvedIssuesCount ?? 0) > 0 && (
              <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                {summary.unresolvedIssuesCount}
              </span>
            )}
          </button>

          {/* Pricing button */}
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-2xs transition-colors hover:bg-gray-50"
            onClick={() => handleOpenPanel('pricing')}
          >
            <DollarSign className="h-3.5 w-3.5" />
            Pricing
          </button>

          {/* History button */}
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-2xs transition-colors hover:bg-gray-50"
            onClick={() => void openHistory()}
          >
            <History className="h-3.5 w-3.5" />
            History
          </button>

          {/* Edit configuration button */}
          <RequirePermission permission="admin:extensions:manage" fallback={null}>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white shadow-2xs transition-colors hover:bg-[#2a45b0]"
              onClick={() => setConfigDrawerOpen(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit configuration
            </button>
          </RequirePermission>
        </div>
      </div>

      {/* 2. Filter Bar (no card): Scope selector, Month period selector with cycle label, Last-modified */}
      <div className="flex flex-col gap-3 py-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <ScopeBar
            agents={agents}
            scope={scope}
            scopeAgentId={scopeAgentId}
            onScopeChange={(s, agentId) => {
              setScope(s)
              setScopeAgentId(agentId)
            }}
            inheriting={inheriting}
          />
          <div className="hidden h-4 w-px bg-gray-200 sm:block" />
          <PeriodSelector
            value={period}
            cycleLabel={summary.cycleLabel}
            onChange={(p) => setPeriod(p)}
          />
        </div>
        <LastModifiedLabel config={config} />
      </div>

      {/* Inherited-state banner */}
      {scope === 'agent' && inheriting && <InheritingBanner />}

      {/* 3. Hero Card replacing the three top cards */}
      <HeroBudgetCard
        totalSpendUsd={summary.totalSpendUsd}
        monthlySpendCapUsd={summary.monthlySpendCapUsd}
        percentUsed={summary.percentUsed}
        projectedSpendUsd={summary.projectedSpendUsd}
        state={summary.state}
        willExceedDate={summary.willExceedDate}
        dailyAverageUsd={summary.dailyAverageUsd}
        daysLeft={summary.daysLeft}
        softAlertThresholdPercent={summary.softAlertThresholdPercent}
        softAlertUsd={summary.softAlertUsd}
        scope={scope}
        agentId={scopeAgentId}
        capExceededAction={summary.capExceededAction}
        resolvedFallbackTarget={summary.resolvedFallbackTarget}
        fallbackWarning={summary.fallbackWarning}
        notifyChannels={summary.notifyChannels}
        providerBanner={breakdown.providerBanner}
        unresolvedIssuesCount={summary.unresolvedIssuesCount}
        estimatedUsageCount={summary.estimatedUsageCount}
        onOpenActivity={() => handleOpenPanel('activity')}
      />

      {/* 4. Card: Model usage & limits (Tabs: By Model | By Provider | By Agent | By Surface) */}
      <ModelUsageTable
        rows={breakdown.rows}
        totals={breakdown.totals}
        loading={tableLoading}
        activeTab={tableTab}
        onTabChange={setTableTab}
        onEditCap={handleOpenEditCap}
        period={period}
        providers={configuredProviderNames}
        selectedProvider={selectedProvider}
        onSelectProvider={setSelectedProvider}
      />

      {/* ── Edit Configuration Drawer ── */}
      <ResourceDrawer
        open={configDrawerOpen}
        onClose={() => setConfigDrawerOpen(false)}
        onSave={handleSaveConfig}
        title={`Budget Configuration — ${scopeTitle}`}
        icon={<Wallet className="h-4 w-4 text-white" />}
        width="xl"
        saving={savingConfig}
        disabled={!dirty}
        saveLabel="Save Settings"
      >
        <DrawerTabs
          tabs={[
            { key: 'config' as const, label: 'Budget & Limits', icon: <SlidersHorizontal className="h-3.5 w-3.5" /> },
            { key: 'test' as const, label: 'Simulation & Test', icon: <FlaskConical className="h-3.5 w-3.5" /> },
          ]}
          active={drawerTab}
          onChange={setDrawerTab}
        />

        <div className="flex flex-col gap-5 pt-2">
          {drawerTab === 'config' && (
            <div className="flex flex-col gap-5">
              {/* Section 1: Monthly Spend & Action */}
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-2xs">
                <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
                  <div>
                    <h5 className="text-xs font-semibold text-gray-900">Monthly Spend Cap</h5>
                    <p className="text-[11px] text-gray-400">Total monthly AI model spending allowed for this scope.</p>
                  </div>
                  <span className="font-mono text-sm font-bold text-[#3454cf]">
                    ${form.monthlySpendCapUsd.toFixed(2)}
                  </span>
                </div>

                <div className="mt-3.5 space-y-3.5">
                  <div>
                    <label className={DRAWER_LABEL_CLASS}>Spend Cap ($ USD / month)</label>
                    <div className="relative mt-1">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-xs font-semibold text-gray-400">
                        $
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="10"
                        value={form.monthlySpendCapUsd}
                        onChange={(e) =>
                          patchForm({ monthlySpendCapUsd: Math.max(0, parseFloat(e.target.value) || 0) })
                        }
                        className={`${DRAWER_INPUT_CLASS} !pl-7`}
                        placeholder="100.00"
                      />
                    </div>
                    {/* Quick presets */}
                    <div className="mt-2 flex items-center gap-1.5">
                      <span className="text-[11px] text-gray-400">Presets:</span>
                      {[25, 50, 100, 250, 500, 1000].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => patchForm({ monthlySpendCapUsd: preset })}
                          className={`rounded-xs px-2 py-0.5 text-[11px] font-medium transition-colors ${
                            form.monthlySpendCapUsd === preset
                              ? 'bg-[#3454cf] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          ${preset}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Soft Alert Threshold */}
                  <div className="rounded-md border border-gray-100 bg-gray-50/70 p-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-gray-700">Soft Alert Warning Threshold</span>
                      <span className="font-semibold text-[#3454cf]">
                        {form.softAlertThresholdPercent}% (${((form.monthlySpendCapUsd * form.softAlertThresholdPercent) / 100).toFixed(2)})
                      </span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="5"
                      value={form.softAlertThresholdPercent}
                      onChange={(e) =>
                        patchForm({ softAlertThresholdPercent: Number(e.target.value) })
                      }
                      className="mt-2 w-full accent-[#3454cf]"
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      Admins are notified when month-to-date spend crosses this threshold.
                    </p>
                  </div>

                  {/* Action when Cap Exceeded */}
                  <div>
                    <label className={DRAWER_LABEL_CLASS}>When Monthly Cap is Exceeded</label>
                    <Select
                      ariaLabel="Action when monthly cap exceeded"
                      value={form.capExceededAction}
                      onChange={(v) =>
                        patchForm({ capExceededAction: v as CapExceededAction })
                      }
                      className="mt-1"
                    >
                      {(meta?.capExceededActions ?? [
                        { value: 'degrade_to_cheaper_model', label: 'Degrade to cheaper model' },
                        { value: 'block_all_requests', label: 'Block all requests' },
                        { value: 'fallback_to_free_models', label: 'Fallback to free models' },
                        { value: 'alert_only', label: 'Alert only' },
                      ]).map((a) => (
                        <option key={a.value} value={a.value}>
                          {a.label}
                        </option>
                      ))}
                    </Select>
                    <p className="mt-1.5 text-[11px] text-gray-500">
                      {meta?.capExceededActions.find((a) => a.value === form.capExceededAction)?.hint ??
                        'Requests exceeding the spend cap will trigger this automated policy.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 2: Rate Limits */}
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-2xs">
                <div className="border-b border-gray-100 pb-2.5">
                  <h5 className="text-xs font-semibold text-gray-900">Live Rate Limits</h5>
                  <p className="text-[11px] text-gray-400">In-memory sliding window limits enforced per minute.</p>
                </div>

                <div className="mt-3.5 space-y-3.5">
                  <div className="grid grid-cols-2 gap-3.5">
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Max Requests / min (RPM)</label>
                      <input
                        type="number"
                        min="1"
                        value={form.rateLimits.maxRequestsPerMinute}
                        onChange={(e) =>
                          patchForm({
                            rateLimits: {
                              ...form.rateLimits,
                              maxRequestsPerMinute: Math.max(1, parseInt(e.target.value) || 1),
                            },
                          })
                        }
                        className={DRAWER_INPUT_CLASS}
                      />
                    </div>
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Max Tokens / min (TPM)</label>
                      <input
                        type="number"
                        min="100"
                        step="1000"
                        value={form.rateLimits.maxTokensPerMinute}
                        onChange={(e) =>
                          patchForm({
                            rateLimits: {
                              ...form.rateLimits,
                              maxTokensPerMinute: Math.max(100, parseInt(e.target.value) || 100),
                            },
                          })
                        }
                        className={DRAWER_INPUT_CLASS}
                      />
                    </div>
                  </div>

                  {/* Rate Limit Alert */}
                  <div className="rounded-md border border-gray-100 bg-gray-50/70 p-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-gray-700">Rate Limit Warning Threshold</span>
                      <span className="font-semibold text-[#3454cf]">
                        {form.rateLimits.softAlertThresholdPercent}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="5"
                      value={form.rateLimits.softAlertThresholdPercent}
                      onChange={(e) =>
                        patchForm({
                          rateLimits: {
                            ...form.rateLimits,
                            softAlertThresholdPercent: Number(e.target.value),
                          },
                        })
                      }
                      className="mt-2 w-full accent-[#3454cf]"
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      Warns admins before live RPM or TPM limits are reached.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 3: Billing Cycle & Timezone */}
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-2xs">
                <div className="border-b border-gray-100 pb-2.5">
                  <h5 className="text-xs font-semibold text-gray-900">Billing Cycle & Timezone</h5>
                  <p className="text-[11px] text-gray-400">Anchor day and timezone for calculating the monthly budget period.</p>
                </div>

                <div className="mt-3.5 space-y-3.5">
                  <div className="grid grid-cols-2 gap-3.5">
                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Cycle Start Day (1–28)</label>
                      <input
                        type="number"
                        min="1"
                        max="28"
                        value={form.billingCycleDay}
                        onChange={(e) =>
                          patchForm({
                            billingCycleDay: Math.min(28, Math.max(1, parseInt(e.target.value) || 1)),
                          })
                        }
                        className={DRAWER_INPUT_CLASS}
                      />
                      <p className="mt-1 text-[11px] text-gray-400">Day of month the budget resets.</p>
                    </div>

                    <div>
                      <label className={DRAWER_LABEL_CLASS}>Billing Timezone</label>
                      <Select
                        ariaLabel="Billing timezone"
                        value={form.timezone}
                        onChange={(v) => patchForm({ timezone: v })}
                        className="mt-1"
                      >
                        {TIMEZONE_OPTIONS.filter((t) => t.value !== AUTO_TIMEZONE).map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </Select>
                      <p className="mt-1 text-[11px] text-gray-400">Determines midnight cutoff for periods.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {drawerTab === 'test' && (
            <TestPanel
              title={
                <>
                  <FlaskConical className="h-3.5 w-3.5" /> What-If Budget Simulation
                </>
              }
              onRun={() => void runTest()}
              running={testing}
              runDisabled={testSpend.trim() === ''}
              result={testResult}
            >
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-semibold text-gray-500">Hypothetical Usage</label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={testSpend}
                    onChange={(e) => setTestSpend(e.target.value)}
                    placeholder="Spend ($), e.g. 120"
                    className={DRAWER_INPUT_CLASS}
                  />
                  <input
                    value={testRpm}
                    onChange={(e) => setTestRpm(e.target.value)}
                    placeholder="RPM (optional)"
                    className={`${DRAWER_INPUT_CLASS} !w-28`}
                  />
                  <input
                    value={testTpm}
                    onChange={(e) => setTestTpm(e.target.value)}
                    placeholder="TPM (optional)"
                    className={`${DRAWER_INPUT_CLASS} !w-28`}
                  />
                </div>
              </div>
            </TestPanel>
          )}

          <LastModifiedLabel config={config} />
        </div>
      </ResourceDrawer>

      {/* ── Version History Drawer ── */}
      <VersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={`Version History — ${scope === 'global' ? 'Global' : `Agent: ${scopeAgentId}`}`}
        versions={versions}
        onRestore={(versionId) => void handleRestore(versionId)}
      />

      {/* ── Model Limit Drawer ── */}
      {editingModelLimit && (
        <ModelLimitDrawer
          open={modelLimitDrawerOpen}
          onClose={() => setModelLimitDrawerOpen(false)}
          onSave={handleSaveModelLimit}
          onDelete={handleDeleteModelLimit}
          model={editingModelLimit.model}
          currentCapUsd={editingModelLimit.currentCap}
          currentAction={editingModelLimit.currentAction}
          currentTargetModel={editingModelLimit.currentTargetModel}
          configuredModels={configuredModelNames}
          saving={savingModelLimit}
        />
      )}

      {/* ── Activity & Pricing Slide-Over Drawer ── */}
      <ActivityPricingDrawer
        open={isActivityPricingOpen}
        onClose={handleClosePanel}
        activeTab={activityPricingTab}
        onTabChange={handleOpenPanel}
        events={activityEvents}
        periodLabel={summary.period}
        pricingItems={pricingItems}
        onSavePricing={handleSavePricing}
        onDeletePricing={handleDeletePricing}
        savingPricing={savingPricing}
      />
    </div>
  )
}
